/**
 * AxLabs Mathematics Experiments - Linear Algebra
 *
 * Collection of experiments exploring linear algebra concepts:
 * matrix operations, determinants, eigenvalues, vector spaces,
 * linear transformations, and solution spaces.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, multiplyMatrices2x2 } from './helpers.js';
// ============================================================================
// MATRIX MULTIPLICATION PROPERTIES
// ============================================================================
export const MatrixMultiplicationProperties = createExperimentFromSchema({
    id: 'matrix-multiplication-properties',
    name: 'Matrix Multiplication Properties',
    domain: 'mathematics',
    description: 'Explore matrix multiplication properties',
    conceptTags: ['Matrix Multiplication', 'Non-commutativity', 'Associativity', 'Identity'],
    difficulty: 4,
    stateVariables: [
        { id: 'a11', name: 'A[1,1]', unit: 'dimensionless', initial: 1, symbol: 'A_{11}' },
        { id: 'a12', name: 'A[1,2]', unit: 'dimensionless', initial: 2, symbol: 'A_{12}' },
        { id: 'a21', name: 'A[2,1]', unit: 'dimensionless', initial: 3, symbol: 'A_{21}' },
        { id: 'a22', name: 'A[2,2]', unit: 'dimensionless', initial: 4, symbol: 'A_{22}' },
        { id: 'ab11', name: 'AB[1,1]', unit: 'dimensionless', initial: 7, symbol: '(AB)_{11}' },
        { id: 'ba11', name: 'BA[1,1]', unit: 'dimensionless', initial: 11, symbol: '(BA)_{11}' },
        { id: 'commutes', name: 'AB = BA', unit: 'dimensionless', initial: 0, symbol: 'C' },
    ],
    parameters: [
        { id: 'b11', name: 'B[1,1]', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'B_{11}' },
        { id: 'b12', name: 'B[1,2]', unit: 'dimensionless', min: -5, max: 5, default: 0, symbol: 'B_{12}' },
        { id: 'b21', name: 'B[2,1]', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'B_{21}' },
        { id: 'b22', name: 'B[2,2]', unit: 'dimensionless', min: -5, max: 5, default: 3, symbol: 'B_{22}' },
        { id: 'rate', name: 'Change Rate', unit: 'dimensionless/s', min: -1, max: 1, default: 0.1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'matrix-mult',
            name: 'Matrix Multiplication',
            latex: '(AB)_{ij} = \\sum_{k=1}^n A_{ik}B_{kj}',
            description: 'Row of first times column of second',
        },
        {
            id: 'non-commutative',
            name: 'Non-commutativity',
            latex: 'AB \\neq BA \\text{ (generally)}',
            description: 'Matrix multiplication is not commutative',
        },
    ],
    derivatives: (state, params) => {
        const b11 = getParam(params, 'b11');
        const b12 = getParam(params, 'b12');
        const b21 = getParam(params, 'b21');
        const b22 = getParam(params, 'b22');
        const rate = getParam(params, 'rate');
        const a11 = getState(state, 'a11');
        const a12 = getState(state, 'a12');
        const a21 = getState(state, 'a21');
        const a22 = getState(state, 'a22');
        const nextA11 = a11 + rate * 0.016;
        const nextA12 = a12 + rate * 0.032;
        const nextA21 = a21 + rate * 0.048;
        const nextA22 = a22 + rate * 0.064;
        const A = [[nextA11, nextA12], [nextA21, nextA22]];
        const B = [[b11, b12], [b21, b22]];
        const AB = multiplyMatrices2x2(A, B);
        const BA = multiplyMatrices2x2(B, A);
        const commutes = Math.abs(AB[0][0] - BA[0][0]) < 0.001 &&
            Math.abs(AB[0][1] - BA[0][1]) < 0.001 &&
            Math.abs(AB[1][0] - BA[1][0]) < 0.001 &&
            Math.abs(AB[1][1] - BA[1][1]) < 0.001 ? 1 : 0;
        return {
            a11: nextA11 - a11,
            a12: nextA12 - a12,
            a21: nextA21 - a21,
            a22: nextA22 - a22,
            ab11: AB[0][0] - getState(state, 'ab11'),
            ba11: BA[0][0] - getState(state, 'ba11'),
            commutes: commutes - getState(state, 'commutes'),
        };
    },
    bounds: {
        a11: { min: -10, max: 10 },
        a22: { min: -10, max: 10 },
    },
});
// ============================================================================
// DETERMINANT GEOMETRIC MEANING
// ============================================================================
export const DeterminantGeometricMeaning = createExperimentFromSchema({
    id: 'determinant-geometric-meaning',
    name: 'Determinant Geometric Meaning',
    domain: 'mathematics',
    description: 'Explore the geometric interpretation of determinants',
    conceptTags: ['Determinant', 'Area Scaling', 'Linear Transformation', 'Orientation'],
    difficulty: 4,
    stateVariables: [
        { id: 'angle', name: 'Angle θ', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'det', name: 'Determinant', unit: 'dimensionless', initial: 1, symbol: '\\det(A)' },
        { id: 'areaRatio', name: 'Area Ratio', unit: 'dimensionless', initial: 1, symbol: '\\frac{Area\'}{Area}' },
        { id: 'scale', name: 'Scale Factor s', unit: 'dimensionless', initial: 1, symbol: 's' },
        { id: 'orientation', name: 'Orientation', unit: 'dimensionless', initial: 1, symbol: 'sgn' },
    ],
    parameters: [
        { id: 'scale', name: 'Base Scale', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 's' },
        { id: 'reflection', name: 'Reflection', unit: 'dimensionless', min: 0, max: 1, default: 0, symbol: 'R' },
        { id: 'angleRate', name: 'Angular Rate', unit: 'rad/s', min: -1, max: 1, default: 0.3, symbol: '\\omega' },
    ],
    equations: [
        {
            id: 'det-area',
            name: 'Determinant as Area',
            latex: '|\\det(A)| = \\text{area scaling factor}',
            description: 'Absolute value of determinant equals area scaling',
        },
        {
            id: 'det-2x2',
            name: '2×2 Determinant',
            latex: '\\det\\begin{pmatrix}a & b \\\\ c & d\\end{pmatrix} = ad - bc',
            description: 'Formula for 2×2 determinant',
        },
    ],
    derivatives: (state, params) => {
        const scale = getParam(params, 'scale');
        const reflection = getParam(params, 'reflection');
        const angleRate = getParam(params, 'angleRate');
        const angle = getState(state, 'angle');
        const nextAngle = angle + angleRate * 0.016;
        // Create transformation matrix
        const cos = Math.cos(nextAngle);
        const sin = Math.sin(nextAngle);
        // Matrix: rotation + scale + possible reflection
        const a = scale * cos;
        const b = scale * sin;
        const c = scale * (-sin);
        const d = scale * cos;
        // Apply reflection (flip across x-axis)
        const finalA = reflection > 0.5 ? -a : a;
        const finalC = reflection > 0.5 ? -c : c;
        const det = finalA * d - b * finalC;
        const areaRatio = Math.abs(det);
        const orientation = det >= 0 ? 1 : -1;
        return {
            angle: angleRate * 0.016,
            det: det - getState(state, 'det'),
            areaRatio: areaRatio - getState(state, 'areaRatio'),
            scale: scale - getState(state, 'scale'),
            orientation: orientation - getState(state, 'orientation'),
        };
    },
    bounds: {
        det: { min: -10, max: 10 },
        areaRatio: { min: 0, max: 10 },
    },
});
// ============================================================================
// EIGENVALUE COMPUTATION
// ============================================================================
export const EigenvalueComputation = createExperimentFromSchema({
    id: 'eigenvalue-computation',
    name: 'Eigenvalue Computation',
    domain: 'mathematics',
    description: 'Compute and visualize eigenvalues and eigenvectors',
    conceptTags: ['Eigenvalues', 'Eigenvectors', 'Characteristic Polynomial', 'Diagonalization'],
    difficulty: 6,
    stateVariables: [
        { id: 'lambda1', name: 'Eigenvalue λ₁', unit: 'dimensionless', initial: 3, symbol: '\\lambda_1' },
        { id: 'lambda2', name: 'Eigenvalue λ₂', unit: 'dimensionless', initial: 2, symbol: '\\lambda_2' },
        { id: 'detLambda', name: 'det(A - λI)', unit: 'dimensionless', initial: 0, symbol: '|A - \\lambda I|' },
        { id: 'trace', name: 'Trace tr(A)', unit: 'dimensionless', initial: 5, symbol: 'tr(A)' },
        { id: 'detA', name: 'Determinant', unit: 'dimensionless', initial: 6, symbol: 'det(A)' },
        { id: 'sumLambda', name: 'Σλᵢ', unit: 'dimensionless', initial: 5, symbol: '\\sum \\lambda_i' },
    ],
    parameters: [
        { id: 'a11', name: 'A[1,1]', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'A_{11}' },
        { id: 'a12', name: 'A[1,2]', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'A_{12}' },
        { id: 'a21', name: 'A[2,1]', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'A_{21}' },
        { id: 'a22', name: 'A[2,2]', unit: 'dimensionless', min: -5, max: 5, default: 3, symbol: 'A_{22}' },
    ],
    equations: [
        {
            id: 'char-poly',
            name: 'Characteristic Polynomial',
            latex: '\\det(A - \\lambda I) = \\lambda^2 - tr(A)\\lambda + det(A) = 0',
            description: 'Polynomial whose roots are eigenvalues',
        },
        {
            id: 'trace-sum',
            name: 'Trace Property',
            latex: '\\sum \\lambda_i = tr(A)',
            description: 'Sum of eigenvalues equals trace',
        },
        {
            id: 'det-product',
            name: 'Determinant Property',
            latex: '\\prod \\lambda_i = det(A)',
            description: 'Product of eigenvalues equals determinant',
        },
    ],
    derivatives: (_state, params) => {
        const a11 = getParam(params, 'a11');
        const a12 = getParam(params, 'a12');
        const a21 = getParam(params, 'a21');
        const a22 = getParam(params, 'a22');
        // Characteristic polynomial: λ² - (a11 + a22)λ + (a11*a22 - a12*a21) = 0
        const trace = a11 + a22;
        const detA = a11 * a22 - a12 * a21;
        // Solve quadratic: λ = (trace ± sqrt(trace² - 4*det)) / 2
        const discriminant = trace * trace - 4 * detA;
        let lambda1 = 0;
        let lambda2 = 0;
        if (discriminant >= 0) {
            const sqrtD = Math.sqrt(discriminant);
            lambda1 = (trace + sqrtD) / 2;
            lambda2 = (trace - sqrtD) / 2;
        }
        else {
            // Complex eigenvalues
            lambda1 = trace / 2;
            lambda2 = Math.sqrt(-discriminant) / 2;
        }
        const sumLambda = lambda1 + lambda2;
        void discriminant; // At eigenvalues, this equals 0 but kept for clarity
        return {
            lambda1: lambda1 - getState(_state, 'lambda1'),
            lambda2: lambda2 - getState(_state, 'lambda2'),
            detLambda: 0 - getState(_state, 'detLambda'),
            trace: trace - getState(_state, 'trace'),
            detA: detA - getState(_state, 'detA'),
            sumLambda: sumLambda - getState(_state, 'sumLambda'),
        };
    },
    bounds: {
        lambda1: { min: -10, max: 10 },
        lambda2: { min: -10, max: 10 },
        trace: { min: -10, max: 10 },
    },
});
// ============================================================================
// VECTOR SPACE BASIS
// ============================================================================
export const VectorSpaceBasis = createExperimentFromSchema({
    id: 'vector-space-basis',
    name: 'Vector Space Basis',
    domain: 'mathematics',
    description: 'Explore basis and dimension of vector spaces',
    conceptTags: ['Basis', 'Dimension', 'Linear Independence', 'Span'],
    difficulty: 5,
    stateVariables: [
        { id: 'v1x', name: 'v₁ x-component', unit: 'dimensionless', initial: 1, symbol: 'v_{1x}' },
        { id: 'v1y', name: 'v₁ y-component', unit: 'dimensionless', initial: 0, symbol: 'v_{1y}' },
        { id: 'v2x', name: 'v₂ x-component', unit: 'dimensionless', initial: 0, symbol: 'v_{2x}' },
        { id: 'v2y', name: 'v₂ y-component', unit: 'dimensionless', initial: 1, symbol: 'v_{2y}' },
        { id: 'det', name: 'Determinant', unit: 'dimensionless', initial: 1, symbol: 'det' },
        { id: 'independent', name: 'Independent', unit: 'dimensionless', initial: 1, symbol: 'I' },
    ],
    parameters: [
        { id: 'angle', name: 'Angle between', unit: 'rad', min: 0, max: Math.PI, default: Math.PI / 2, symbol: '\\theta' },
        { id: 'length1', name: '|v₁|', unit: 'dimensionless', min: 0.5, max: 5, default: 1, symbol: '|v_1|' },
        { id: 'length2', name: '|v₂|', unit: 'dimensionless', min: 0.5, max: 5, default: 1, symbol: '|v_2|' },
    ],
    equations: [
        {
            id: 'basis-def',
            name: 'Basis Definition',
            latex: '\\text{Basis if linearly independent and spans space}',
            description: 'Linearly independent vectors that span the space',
        },
        {
            id: 'independence-det',
            name: 'Independence Test',
            latex: '\\text{Linearly independent } \\iff \\det([v_1\\ v_2]) \\neq 0',
            description: 'Vectors are independent if matrix determinant is non-zero',
        },
    ],
    derivatives: (_state, params) => {
        const angle = getParam(params, 'angle');
        const length1 = getParam(params, 'length1');
        const length2 = getParam(params, 'length2');
        const v1x = length1;
        const v1y = 0;
        const v2x = length2 * Math.cos(angle);
        const v2y = length2 * Math.sin(angle);
        const det = v1x * v2y - v1y * v2x;
        const independent = Math.abs(det) > 0.001 ? 1 : 0;
        return {
            v1x: v1x - getState(_state, 'v1x'),
            v1y: v1y - getState(_state, 'v1y'),
            v2x: v2x - getState(_state, 'v2x'),
            v2y: v2y - getState(_state, 'v2y'),
            det: det - getState(_state, 'det'),
            independent: independent - getState(_state, 'independent'),
        };
    },
    bounds: {
        det: { min: -25, max: 25 },
    },
});
// ============================================================================
// LINEAR TRANSFORMATION EFFECTS
// ============================================================================
export const LinearTransformationEffects = createExperimentFromSchema({
    id: 'linear-transformation-effects',
    name: 'Linear Transformation Effects',
    domain: 'mathematics',
    description: 'Visualize effects of linear transformations on the plane',
    conceptTags: ['Linear Transformations', 'Matrix Transformations', 'Scaling', 'Shear'],
    difficulty: 4,
    stateVariables: [
        { id: 'theta', name: 'Transform Angle', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'unitArea', name: 'Unit Square Area', unit: 'dimensionless', initial: 1, symbol: 'A' },
        { id: 'transformedArea', name: 'Transformed Area', unit: 'dimensionless', initial: 1, symbol: 'A\'' },
        { id: 'areaScale', name: 'Area Scale', unit: 'dimensionless', initial: 1, symbol: '|\\det|' },
        { id: 'corner1x', name: '(1,0) → x', unit: 'dimensionless', initial: 1, symbol: 'T(1,0)_x' },
        { id: 'corner1y', name: '(1,0) → y', unit: 'dimensionless', initial: 0, symbol: 'T(1,0)_y' },
    ],
    parameters: [
        { id: 'transType', name: 'Transform Type', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'T' },
        { id: 'param1', name: 'Parameter 1', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'p_1' },
        { id: 'param2', name: 'Parameter 2', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'p_2' },
        { id: 'animRate', name: 'Animation Rate', unit: 'dimensionless/s', min: -1, max: 1, default: 0.2, symbol: 'r' },
    ],
    equations: [
        {
            id: 'linear-trans-def',
            name: 'Linear Transformation',
            latex: 'T(\\vec{x}) = A\\vec{x}',
            description: 'Matrix multiplication defines linear transformation',
        },
        {
            id: 'area-scale',
            name: 'Area Scaling',
            latex: '\\text{Area}(T(S)) = |\\det A| \\cdot \\text{Area}(S)',
            description: 'Determinant gives area scaling factor',
        },
    ],
    derivatives: (state, params) => {
        const transType = Math.round(getParam(params, 'transType'));
        const p1 = getParam(params, 'param1');
        const p2 = getParam(params, 'param2');
        const animRate = getParam(params, 'animRate');
        const theta = getState(state, 'theta');
        const nextTheta = theta + animRate * 0.016;
        let a = 1, b = 0, c = 0, d = 1;
        switch (transType) {
            case 0: // Rotation
                a = Math.cos(nextTheta);
                b = -Math.sin(nextTheta);
                c = Math.sin(nextTheta);
                d = Math.cos(nextTheta);
                break;
            case 1: // Scale
                a = p1;
                b = 0;
                c = 0;
                d = p2;
                break;
            case 2: // Shear X
                a = 1;
                b = Math.sin(nextTheta);
                c = 0;
                d = 1;
                break;
            case 3: // Custom
                a = p1;
                b = Math.sin(nextTheta);
                c = Math.cos(nextTheta);
                d = p2;
                break;
            case 4: // Reflection
                a = Math.cos(2 * nextTheta);
                b = Math.sin(2 * nextTheta);
                c = Math.sin(2 * nextTheta);
                d = -Math.cos(2 * nextTheta);
                break;
        }
        const det = a * d - b * c;
        const areaScale = Math.abs(det);
        const transformedArea = areaScale;
        // Transform of (1, 0)
        const corner1x = a;
        const corner1y = c;
        return {
            theta: animRate * 0.016,
            unitArea: 0,
            transformedArea: transformedArea - getState(state, 'transformedArea'),
            areaScale: areaScale - getState(state, 'areaScale'),
            corner1x: corner1x - getState(state, 'corner1x'),
            corner1y: corner1y - getState(state, 'corner1y'),
        };
    },
    bounds: {
        areaScale: { min: 0, max: 20 },
        transformedArea: { min: 0, max: 20 },
    },
});
// ============================================================================
// MATRIX INVERSE EXISTENCE
// ============================================================================
export const MatrixInverseExistence = createExperimentFromSchema({
    id: 'matrix-inverse-existence',
    name: 'Matrix Inverse Existence',
    domain: 'mathematics',
    description: 'Explore conditions for matrix invertibility',
    conceptTags: ['Matrix Inverse', 'Invertible Matrix', 'Singular Matrix', 'Determinant'],
    difficulty: 4,
    stateVariables: [
        { id: 'a11', name: 'A[1,1]', unit: 'dimensionless', initial: 1, symbol: 'A_{11}' },
        { id: 'det', name: 'Determinant', unit: 'dimensionless', initial: 1, symbol: '\\det(A)' },
        { id: 'invertible', name: 'Invertible', unit: 'dimensionless', initial: 1, symbol: 'Inv' },
        { id: 'inv11', name: 'A⁻¹[1,1]', unit: 'dimensionless', initial: 1, symbol: '(A^{-1})_{11}' },
        { id: 'cond', name: 'Condition Number', unit: 'dimensionless', initial: 1, symbol: '\\kappa(A)' },
    ],
    parameters: [
        { id: 'determinant', name: 'Determinant', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'det' },
        { id: 'singular', name: 'Near Singular', unit: 'dimensionless', min: 0, max: 1, default: 0, symbol: 'S' },
    ],
    equations: [
        {
            id: 'inverse-existence',
            name: 'Inverse Existence',
            latex: 'A^{-1} \\text{ exists } \\iff \\det(A) \\neq 0',
            description: 'Matrix is invertible iff determinant is non-zero',
        },
        {
            id: 'inverse-2x2',
            name: '2×2 Inverse Formula',
            latex: 'A^{-1} = \\frac{1}{\\det A}\\begin{pmatrix}d & -b \\\\ -c & a\\end{pmatrix}',
            description: 'Formula for inverse of 2×2 matrix',
        },
    ],
    derivatives: (_state, params) => {
        const determinant = getParam(params, 'determinant');
        const singular = getParam(params, 'singular');
        const actualDet = singular > 0.5 ? determinant * 0.001 : determinant;
        const invertible = Math.abs(actualDet) > 0.0001 ? 1 : 0;
        const a11 = 2;
        const a22 = 2;
        // For this matrix, det = a11*a22 - a12*a21 = 4 - 3 = 1
        // Scale to match target determinant
        void Math.abs(actualDet);
        const inv11 = invertible > 0.5 ? (a22 / actualDet) : 0;
        const cond = Math.abs(actualDet) > 0.001 ? Math.abs(a11 + a22) / Math.abs(actualDet) : 1000;
        return {
            a11: 0,
            det: actualDet - getState(_state, 'det'),
            invertible: invertible - getState(_state, 'invertible'),
            inv11: inv11 - getState(_state, 'inv11'),
            cond: cond - getState(_state, 'cond'),
        };
    },
    bounds: {
        det: { min: -5, max: 5 },
        cond: { min: 0, max: 1000 },
    },
});
// ============================================================================
// SYSTEM SOLUTION SPACE
// ============================================================================
export const SystemSolutionSpace = createExperimentFromSchema({
    id: 'system-solution-space',
    name: 'System Solution Space',
    domain: 'mathematics',
    description: 'Explore solution spaces of linear systems',
    conceptTags: ['Linear Systems', 'Solution Space', 'Homogeneous', 'Rank', 'Null Space'],
    difficulty: 6,
    stateVariables: [
        { id: 'rank', name: 'Matrix Rank', unit: 'dimensionless', initial: 2, symbol: 'rank(A)' },
        { id: 'nullity', name: 'Nullity', unit: 'dimensionless', initial: 0, symbol: 'nullity(A)' },
        { id: 'numSolutions', name: 'Solution Type', unit: 'dimensionless', initial: 1, symbol: 'N_{sol}' },
        { id: 'freeVars', name: 'Free Variables', unit: 'dimensionless', initial: 0, symbol: 'free' },
        { id: 'determinant', name: 'Determinant', unit: 'dimensionless', initial: 1, symbol: '\\det(A)' },
    ],
    parameters: [
        { id: 'a11', name: 'A[1,1]', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'A_{11}' },
        { id: 'a12', name: 'A[1,2]', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'A_{12}' },
        { id: 'a21', name: 'A[2,1]', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'A_{21}' },
        { id: 'a22', name: 'A[2,2]', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'A_{22}' },
    ],
    equations: [
        {
            id: 'rank-nullity',
            name: 'Rank-Nullity Theorem',
            latex: 'rank(A) + nullity(A) = n',
            description: 'For n×n matrix, rank plus nullity equals n',
        },
        {
            id: 'solution-types',
            name: 'Solution Types',
            latex: '\\det(A) \\neq 0: \\text{unique} \\quad \\det(A) = 0: \\infty \\text{ or none}',
            description: 'Non-singular: unique solution, Singular: none or infinite',
        },
    ],
    derivatives: (_state, params) => {
        const a11 = getParam(params, 'a11');
        const a12 = getParam(params, 'a12');
        const a21 = getParam(params, 'a21');
        const a22 = getParam(params, 'a22');
        const det = a11 * a22 - a12 * a21;
        let rank = 2;
        let nullity = 0;
        let numSolutions = 1; // 1 = unique, 0 = none, 2 = infinite
        if (Math.abs(det) < 0.001) {
            // Matrix is singular
            if (Math.abs(a11) + Math.abs(a12) + Math.abs(a21) + Math.abs(a22) < 0.001) {
                rank = 0;
                nullity = 2;
            }
            else if (Math.abs(a11 * a12 - a12 * a11) < 0.001 && Math.abs(a21 * a22 - a22 * a21) < 0.001) {
                // Rows are proportional
                rank = 1;
                nullity = 1;
            }
            numSolutions = 2; // Infinite (for homogeneous) or none
        }
        const freeVars = nullity;
        return {
            rank: rank - getState(_state, 'rank'),
            nullity: nullity - getState(_state, 'nullity'),
            numSolutions: numSolutions - getState(_state, 'numSolutions'),
            freeVars: freeVars - getState(_state, 'freeVars'),
            determinant: det - getState(_state, 'determinant'),
        };
    },
    bounds: {
        rank: { min: 0, max: 2 },
        nullity: { min: 0, max: 2 },
    },
});
// ============================================================================
// EXPORT ALL LINEAR ALGEBRA EXPERIMENTS
// ============================================================================
export const ALL_LINEAR_ALGEBRA = [
    MatrixMultiplicationProperties,
    DeterminantGeometricMeaning,
    EigenvalueComputation,
    VectorSpaceBasis,
    LinearTransformationEffects,
    MatrixInverseExistence,
    SystemSolutionSpace,
];
//# sourceMappingURL=linear-algebra.js.map